import {
  getButtonPadding,
  getTextDecoration,
} from '~/actions/triggers/helpers';
import { hexToRgb } from '~/helpers/converters/hexToRgb';
import { getFullImagePath } from '~/utils/getFullImagePath';
import { FontWeight, NotificationConfig } from './types';

const FontWeightValues: { [key in FontWeight]: number } = {
  'Thin': 100,
  'Extra Light': 200,
  'Light': 300,
  'Normal': 400,
  'Medium': 500,
  'Semi-Bold': 600,
  'Bold': 700,
  'Extra Bold': 800,
  'Black': 900,
};

const fonts = [
  'Inter:wght@100;200;300;400;500;600;700;800;900&display=swap',
  'Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap',
  'PT+Sans:ital,wght@0,400;0,700;1,400;1,700&display=swap',
  'PT+Serif:ital,wght@0,400;0,700;1,400;1,700&display=swap',
  'Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap',
  'Roboto+Slab:wght@100;200;300;400;500;600;700;800;900&display=swap',
  'Roboto+Condensed:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap',
  'Oswald:wght@200;300;400;500;600;700&display=swap',
  'Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap',
  'Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap',
  'Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  'Exo+2:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  'Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  'Pacifico&display=swap',
  'Raleway:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  'Fira+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  'Philosopher:ital,wght@0,400;0,700;1,400;1,700&display=swap',
  'Neucha&display=swap',
  'Yeseva+One&display=swap',
];

interface IGetCustomCSS {
  _: string;
  iterator: number;
  isMobile: boolean;
  configuration: NotificationConfig;
}

export const getCustomCSS = ({
  _,
  iterator,
  isMobile,
  configuration,
}: IGetCustomCSS) => {
  const {
    backgroundColor = '#fff',
    borderRadius = 12,
    opacity = 0,

    closeButtonColor = '#fff',
    closeIconColor = '#4d4d4d',

    image,

    buttonBackgroundColor = '#5886f5',
    buttonWithBackgroundColor = true,
    buttonBorderColor = '#fff',
    buttonBorderOpacity = 0,
    buttonBorderWidth = 1,
    buttonBorderRadius = 6,
    buttonFontFamily = 'Inter',
    buttonFontItalic = false,
    buttonFontSize = 16,
    buttonMobileFontSize = 16,
    buttonFontWeight = 'Normal',
    buttonOpacity = 0,
    buttonSize = 'large',
    buttonText,
    buttonTextColor = '#fff',
    buttonTextOpacity = 0,
    buttonTextUnderline = false,
    buttonTextLineThrough = false,
    buttonWithBorder = false,
    isButtonBlockWidth = true,

    couponBackgroundColor = 'transparent',
    couponWithBorder = true,
    couponBorderColor = '#808080',
    couponBorderOpacity = 0,
    couponBorderWidth = 1,
    couponFontFamily = 'Inter',
    couponFontItalic = false,
    couponFontSize = 36,
    couponFontWeight = 'Medium',
    couponMobileFontSize = 24,
    couponOpacity = 0,
    couponTextColor = '#000',
    couponTextUnderline = false,
    couponTextLineThrough = false,

    expirationDateColor = '#000',
    expirationDateFontFamily = 'Inter',
    expirationDateFontItalic = false,
    expirationDateFontSize = 16,
    expirationDateFontWeight = 'Normal',
    expirationDateOpacity = 0,
    expirationDateTextUnderline = false,
    expirationDateTextLineThrough = false,
    expirationMobileDateFontSize = 14,

    hasImage,

    messageColor = '#808080',
    messageFontFamily = 'Inter',
    messageFontItalic = false,
    messageFontSize = 18,
    messageFontWeight = 'Normal',
    messageMobileFontSize = 14,
    messageOpacity = 0,
    messageTextUnderline = false,
    messageTextLineThrough = false,

    titleColor = '#000',
    titleFontFamily = 'Inter',
    titleFontItalic = false,
    titleFontSize = 24,
    titleFontWeight = 'Medium',
    titleMobileFontSize = 18,
    titleOpacity = 0,
    titleTextUnderline = false,
    titleTextLineThrough = false,
  } = configuration;

  return `
  .${_}notification-${iterator} .${_}privacy-policy-box {
      display: none;
      background: #FFFFFF;
      border-radius: 12px;
      width: 680px;
      min-width: 680px;
      padding: 16px 0 32px 0;
      z-index: 9999;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box._visible {
      display: block;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box .${_}privacy-policy-box__close-icon {
      width: 24px;
      height: 24px;
      margin: 0 16px 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #B3B3B3;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box .${_}privacy-policy-box__heading {
      font-family: Inter;
      font-weight: bold;
      font-size: 24px;
      line-height: 32px;
      letter-spacing: -0.02em;
      color: #000000;
      margin: -7px 0 13px 50px;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box .${_}privacy-policy-box__checkbox {
      margin-left: 29px;
      margin-right: 50px;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box span.${_}checkbox__square {
      border-radius: 5px;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box .${_}checkbox__text {
      font-family: Inter;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.03em;
      color: #4D4D4D;
      margin: 2px 0 0 -3px;
      min-width: 582px;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box .${_}privacy-policy-box__unnecessary-checkbox {
      margin-top: -1px;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box .${_}privacy-policy-box__unnecessary-checkbox .${_}checkbox__text {
      margin-top: 1px;
    }

    .${_}notification-${iterator} .${_}privacy-policy-box .${_}privacy-policy-box__info {
      font-family: Inter;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.03em;
      color: #00BD7E;
      margin: 0 0 16px 50px;
    }

    @media (max-width: 700px) {
      .${_}notification-${iterator} .${_}privacy-policy-box {
        width: 100%;
        min-width: 100%;
        max-width: 100%;
        max-height: 100%;
        overflow: auto;
      }

      .${_}notification-${iterator} .${_}privacy-policy-box .${_}checkbox__text {
        min-width: unset;
      }

      .${_}notification-${iterator} .${_}privacy-policy-box .${_}privacy-policy-box__checkbox {
        margin-right: 20px;
      }
    }

    .${_}notification-${iterator}._position-center-left._mobile,
    .${_}notification-${iterator}._position-center-right._mobile,
    .${_}notification-${iterator}._position-center._mobile {
      top: 9%;
    }

    .${_}notification-${iterator} .${_}notification__box {
      background-color: ${hexToRgb(backgroundColor, opacity)};
      background-image: ${
        hasImage && getFullImagePath(image)
          ? `url(${getFullImagePath(image)})`
          : 'none'
      };
      background-position: center;
      background-repeat: no-repeat;
      border-radius: ${borderRadius}px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, .08);
      display: flex;
      flex-direction: column;
      font-family: 'Inter', sans-serif;
      overflow: hidden;
      position: absolute;
    }

    .${_}notification-${iterator}._position-center-left._mobile,
    .${_}notification-${iterator}._position-center-right._mobile,
    .${_}notification-${iterator}._position-center._mobile {
      top: 9%;
    }

    .${_}notification-${iterator} .${_}notification__box._position-top-left,
    .${_}notification-${iterator} .${_}notification__box._position-top-center,
    .${_}notification-${iterator} .${_}notification__box._position-top-right {
      top: 48px;
    }

    .${_}notification-${iterator} .${_}notification__box._position-top-right,
    .${_}notification-${iterator} .${_}notification__box._position-center-right,
    .${_}notification-${iterator} .${_}notification__box._position-bottom-right {
      right: 48px;
    }

    .${_}notification-${iterator} .${_}notification__box._position-bottom-left,
    .${_}notification-${iterator} .${_}notification__box._position-bottom-center,
    .${_}notification-${iterator} .${_}notification__box._position-bottom-right {
      bottom: 48px;
    }

    .${_}notification-${iterator} .${_}notification__box._position-bottom-left,
    .${_}notification-${iterator} .${_}notification__box._position-center-left,
    .${_}notification-${iterator} .${_}notification__box._position-top-left {
      left: 48px;
    }

    .${_}notification-${iterator} .${_}notification__close {
      align-items: center;
      background-color: ${closeButtonColor};
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      height: 36px;
      justify-content: center;
      position: absolute;
      right: 16px;
      top: 16px;
      width: 36px;
      z-index: 1;
    }

    .${_}notification-${iterator} .${_}notification-close__icon {
      align-items: center;
      color: ${closeIconColor};
      display: flex;
      height: 21px !important;
      justify-content: center;
      width: 21px !important;
      position: unset;
    }

    .${_}notification-${iterator} .${_}notification__box._image-size-original {
      background-size: auto auto;
    }

    .${_}notification-${iterator} .${_}notification__box._image-size-fill {
      background-size: cover;
    }

    .${_}notification-${iterator} .${_}notification__box._image-size-fit {
      background-size: contain;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-bottom-left {
      background-position: bottom left;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-bottom-center{
      background-position: bottom center;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-bottom-right {
      background-position: bottom right;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-center-left {
      background-position: center left;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-center {
      background-position: center;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-center-right {
      background-position: center right;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-top-left {
      background-position: top left;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-top-center {
      background-position: top center;
    }

    .${_}notification-${iterator} .${_}notification__box._image-position-top-right {
      background-position: top right;
    }

    .${_}notification-${iterator} .${_}notification__body {
      align-items: center;
      display: flex;
      flex-direction: column;
      padding: 24px 40px 48px;
    }

    .${_}notification-${iterator} .${_}notification__body._alignment-bottom-center {
      padding: 48px 40px 48px;
    }

    .${_}notification-${iterator} .${_}notification__body._alignment-bottom-left,
    .${_}notification-${iterator} .${_}notification__body._alignment-top-left,
    .${_}notification-${iterator} .${_}notification__body._alignment-center-left {
      padding: 48px 40px 48px;
      align-items: flex-start;
    }

    .${_}notification-${iterator} .${_}notification__body._alignment-bottom-left .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__body._alignment-top-left .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__body._alignment-center-left .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__body._alignment-bottom-left .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__body._alignment-top-left .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__body._alignment-center-left .${_}notification__message {
      text-align: left;
    }

    .${_}notification-${iterator} .${_}notification__body._alignment-bottom-right,
    .${_}notification-${iterator} .${_}notification__body._alignment-top-right,
    .${_}notification-${iterator} .${_}notification__body._alignment-center-right {
      padding: 48px 40px 48px;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box .${_}notification__body._alignment-bottom-right .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__box .${_}notification__body._alignment-top-right .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__box .${_}notification__body._alignment-center-right .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__box .${_}notification__body._alignment-bottom-right .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box .${_}notification__body._alignment-top-right .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box .${_}notification__body._alignment-center-right .${_}notification__message {
      text-align: right;
    }

    .${_}notification-${iterator} .${_}notification__body._alignment-bottom-center,
    .${_}notification-${iterator} .${_}notification__body._alignment-center,
    .${_}notification-${iterator} .${_}notification__body._alignment-top-center {
      padding: 48px 40px 48px;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-top-right {
      padding: 48px 40px 269px;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-top-right .${_}notification__message {
      text-align: right;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-top-left {
      padding: 48px 40px 269px;
      align-items: flex-start;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-top-left .${_}notification__message {
      text-align: left;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-top-center {
      padding: 48px 40px 269px;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-center-left {
      padding: 135px 40px 135px 40px;
      align-items: flex-start;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-center-left .${_}notification__message {
      text-align: left;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-center-right {
      padding: 135px 40px 135px 40px;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-center-right .${_}notification__message {
      text-align: right;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-center {
      padding: 135px 40px 135px 40px;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-bottom-right {
      padding: 269px 40px 48px;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-bottom-center {
      padding: 269px 40px 48px;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image ._alignment-bottom-left {
      padding: 269px 40px 48px;
      align-items: flex-start;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-top-right {
      padding: 37px 30px 190px;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-top-left {
      padding: 37px 30px 190px;
      align-items: flex-start;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-top-center {
      padding: 37px 30px 190px;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-center-left {
      padding: 95px 30px 95px;
      align-items: flex-start;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-center-right {
      padding: 95px 30px 95px;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-center {
      padding: 95px 30px 95px;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-bottom-right {
      padding: 190px 30px 37px;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-bottom-center {
      padding: 190px 30px 37px;
    }

    .${_}notification-${iterator} .${_}notification__box._has-image._mobile ._alignment-bottom-left {
      padding: 190px 30px 37px;
      align-items: flex-start;
    }

    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body {
      align-items: flex-start;
      padding: 48px 53px 60px 48px;
      width: 473px;
    }

    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-bottom-right,
    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-top-right,
    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-center-right {
      margin-left: auto;
      align-items: flex-end;
    }

    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-bottom-center,
    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-center,
    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-top-center {
      margin: 0 auto;
      align-items: center;
    }

    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-bottom-center > div,
    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-center > div,
    .${_}notification-${iterator} .${_}notification__box._horizontal:not(._mobile) .${_}notification__body._alignment-top-center > div {
      text-align: center;
    }

    .${_}notification-${iterator} .${_}notification__title {
      color: ${hexToRgb(titleColor, titleOpacity)};
      font-family: "${titleFontFamily}";
      font-size: ${
        isMobile ? `${titleMobileFontSize}px` : `${titleFontSize}px`
      };
      font-style: ${titleFontItalic ? 'italic' : 'normal'};
      font-weight: ${FontWeightValues[titleFontWeight as FontWeight]};
      line-height: 32px;
      letter-spacing: -.02em;
      margin-bottom: 14px;
      text-align: center;
      text-decoration: ${getTextDecoration(
        titleTextUnderline,
        titleTextLineThrough
      )};
      word-wrap: break-word;
    }

    .${_}notification-${iterator} .${_}notification__message {
      color: ${hexToRgb(messageColor, messageOpacity)};
      font-family: "${messageFontFamily}";
      font-size: ${
        isMobile ? `${messageMobileFontSize}px` : `${messageFontSize}px`
      };
      font-style: ${messageFontItalic ? 'italic' : 'normal'};
      font-weight: ${FontWeightValues[messageFontWeight as FontWeight]};
      letter-spacing: -.02em;
      line-height: 24px;
      margin-bottom: 24px;
      text-align: center;
      text-decoration: ${getTextDecoration(
        messageTextUnderline,
        messageTextLineThrough
      )};
      word-wrap: break-word;
      max-width: 100%;
    }

    .${_}notification-${iterator} .${_}notification__coupon {
      background-color: ${
        couponBackgroundColor
          ? `${hexToRgb(couponBackgroundColor, couponOpacity)}`
          : 'transparent'
      };
      outline: ${
        couponWithBorder
          ? `${couponBorderWidth}px dashed ${hexToRgb(
              couponBorderColor,
              couponBorderOpacity
            )}`
          : 'none'
      };
      min-height: 40px;
      margin-bottom: 16px;
      min-width: 180px;
      max-width: 370px;
      position: relative;
      opacity: ${couponOpacity};
      overflow: hidden;
      width: 100%;
      text-align: center;
    }

    .${_}notification-${iterator} .${_}notification__coupon-text {
      color: ${couponTextColor};
      font-family: "${couponFontFamily}";
      font-size: ${
        isMobile ? `${couponMobileFontSize}px` : `${couponFontSize}px`
      };
      font-style: ${couponFontItalic ? 'italic' : 'normal'};
      font-weight: ${FontWeightValues[couponFontWeight as FontWeight]};
      letter-spacing: -.02em;
      line-height: 40px;
      margin: 10px 0;
      text-align: center;
      text-decoration: ${getTextDecoration(
        couponTextUnderline,
        couponTextLineThrough
      )};
      word-wrap: break-word;
    }

    .${_}notification-${iterator} .${_}notification__body .${_}notification__coupon-text._hidden {
      clip-path: polygon(0 0, 60% 0, 40% 100%, 0% 100%) !important;
      display: block;
    }

    .${_}notification-${iterator} .${_}notification__expiration {
      color: ${hexToRgb(expirationDateColor, expirationDateOpacity)};
      display: flex;
      flex-direction: row;
      font-family: "${expirationDateFontFamily}";
      font-size: ${
        isMobile
          ? `${expirationMobileDateFontSize}px`
          : `${expirationDateFontSize}px`
      };
      font-style: ${expirationDateFontItalic ? 'italic' : 'normal'};
      font-weight: ${FontWeightValues[expirationDateFontWeight as FontWeight]};
      margin-bottom: 24px;
      text-decoration: ${getTextDecoration(
        expirationDateTextUnderline,
        expirationDateTextLineThrough
      )};
    }

    .${_}notification-${iterator} .${_}notification__expiration-icon {
      align-items: center;
      display: flex;
      height: 24px;
      justify-content: center;
      margin-right: 4px;
      width: 24px;
    }

    .${_}notification-${iterator} .${_}notification__expiration-icon svg {
      color: ${hexToRgb(expirationDateColor, expirationDateOpacity)};
      width: 18px;
      height: 18px;
      display: block;
    }

    .${_}notification-${iterator} .${_}notification__expiration-date {
      letter-spacing: -.02em;
      line-height: 24px;
    }

    .${_}notification-${iterator} .${_}notification__button {
      align-items: center;
      background-color: ${
        buttonWithBackgroundColor
          ? hexToRgb(buttonBackgroundColor, buttonOpacity)
          : 'transparent'
      } !important;
      background-image: none !important;
      border: ${
        buttonWithBorder
          ? `${buttonBorderWidth}px solid ${hexToRgb(
              buttonBorderColor,
              buttonBorderOpacity
            )}`
          : 'none'
      };
      border-radius: ${buttonBorderRadius}px;
      box-sizing: border-box;
      color: ${hexToRgb(buttonTextColor, buttonTextOpacity)};
      cursor: pointer;
      display: inline-flex;
      flex-shrink: 0;
      font-family: "${buttonFontFamily}";
      font-size: ${isMobile ? buttonMobileFontSize : buttonFontSize}px;
      font-style: ${buttonFontItalic ? 'italic' : 'normal'};
      font-weight: ${FontWeightValues[buttonFontWeight as FontWeight]};
      justify-content: center;
      outline: none;
      overflow: hidden;
      padding: ${getButtonPadding(buttonSize)};
      position: relative;
      text-decoration: ${getTextDecoration(
        buttonTextUnderline,
        buttonTextLineThrough
      )};
      transition-duration: 100ms;
      transition-property: color, background-color, box-shadow, border-color;
      transition-timing-function: ease;
      user-select: none;
      vertical-align: top;
      white-space: nowrap;
      max-width: 100%;
    }

    .${_}notification-${iterator} .${_}notification__button:disabled {
      pointer-events: none;
      background-color: rgb(219, 219, 219, ${buttonOpacity}) !important;
    }

    .${_}notification-${iterator} .${_}notification__button::before,
    .${_}notification-${iterator} .${_}notification__button::after {
      height: 100%;
      background: inherit;
      display: block;
      position: absolute;
      content: "";
    }

    .${_}notification-${iterator} .${_}notification__button::before {
      left: 0;
    }

    .${_}notification-${iterator} .${_}notification__button::after {
      right: 0;
    }

    .${_}notification-${iterator} .${_}notification__button._block {
      width: 100%;
    }

    .${_}notification-${iterator} .${_}notification__button:active {
      filter: brightness(90%);
      transition: filter 50ms ease;
    }

    .${_}notification-${iterator} .${_}notification__button._size-small {
      min-height: 40px;
      font-size: 14px;
    }

    .${_}notification-${iterator} .${_}notification__button._size-small::before,
    .${_}notification-${iterator} .${_}notification__button._size-small::after {
      width: 20px;
    }

    .${_}notification-${iterator} .${_}notification__button._size-small .button-like__container {
      margin: auto 7px;
    }

    .${_}notification-${iterator} .${_}notification__button._size-medium {
      min-height: 48px;
    }

    .${_}notification-${iterator} .${_}notification__button._size-medium::before,
    .${_}notification-${iterator} .${_}notification__button._size-medium::after {
      width: 24px;
    }

    .${_}notification-${iterator} .${_}notification__button._size-medium .button-like__container {
      margin: auto 11px;
    }

    .${_}notification-${iterator} .${_}notification__button._size-large {
      min-height: 60px;
      font-size: 16px;
    }

    .${_}notification-${iterator} .${_}notification__button._size-large::before,
    .${_}notification-${iterator} .${_}notification__button._size-large::after {
      width: 30px;
    }

    .${_}notification-${iterator} .${_}notification__box._horizontal {
      flex-direction: row;
      width: 800px;
    }

    .${_}notification-${iterator} .${_}notification__box._horizontal .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box._horizontal .${_}notification__title {
      text-align: left;
    }


    .${_}notification-${iterator} .${_}notification__box._horizontal .${_}notification__button {
      ${isButtonBlockWidth ? 'min-width: 329px' : ''};
    }

    .${_}notification-${iterator} .${_}notification__box._mobile {
      flex-direction: column;
      top: 151px;
      transform: translate(-50%, 0);
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__close {
      top: 12px;
      right: 10px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__body {
      padding: 37px 34px 37px;
      width: auto;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-top-right .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-center-right .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-bottom-right .${_}notification__message {
      text-align: right;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-top-right .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-center-right .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-bottom-right .${_}notification__title {
      text-align: right;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-top-left .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-center-left .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-bottom-left .${_}notification__message {
      text-align: left;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-top-left .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-center-left .${_}notification__title,
    .${_}notification-${iterator} .${_}notification__box._mobile ._alignment-bottom-left .${_}notification__title {
      text-align: left;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__title {
      font-size: ${titleMobileFontSize}px;
      margin-bottom: 12px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__coupon-text {
      font-size: 24px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__coupon {
      padding: 3px 32px;
      min-height: 48px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__message,
    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__coupon,
    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__expiration {
      margin-bottom: 16px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__message {
      font-size: ${messageMobileFontSize}px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__expiration-date {
      font-size: 14px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__button {
      font-size: ${buttonMobileFontSize}px;
    }

    .${_}notification-${iterator} .${_}notification__box._mobile .${_}notification__button {
      padding-bottom: 13px;
      padding-top: 13px;
      min-width: auto;
      width: 100%;
    }
  `;
};
